import axios from 'axios';
import { i18n } from '@/locales/i18n';
import { get as getToken, remove as removeToken } from '@/utils/token';
import {
    config as api,
    options,
    transformResponse,
    error,
} from '@/config/api';
import { reportError, report, reportToken } from '@/utils/monitor';
import { useComplianceWall } from '@/hooks/compliance';

const { showComplianceWall } = useComplianceWall();

// axios 实例
export const service = axios.create(options);
export const { CancelToken } = axios;

/**
 * 匹配接口配置
 *
 * @param {string} url 请求链接
 * @return {(null|Object)}
 */
const match = (url) => {
    let result = null;

    Object.keys(api).some((key) => {
        if (url.indexOf(key) === 0) {
            result = typeof api[key] === 'string'
                ? { url: api[key], key }
                : { ...api[key], key };

            return true;
        }

        return false;
    });

    return result;
};

/**
 * 合并对象
 *
 * @param {Object} context 目标数据
 * @param {Object} data 原始数据
 * @param {Object} args 方法参数
 * @return {Object}
 */
const mergeObject = (context, data, args) => {
    if (typeof context === 'undefined') {
        return { ...data };
    }

    if (typeof context === 'object') {
        return {
            ...context,
            ...data,
        };
    }

    if (typeof context === 'function') {
        return context(data, args);
    }

    return {};
};

// 请求拦截器
service.interceptors.request.use(async (config) => {
    const matched = match(config.url);

    // 打上请求时间
    config.requestTime = Date.now();

    // 添加国际化
    config.headers = {
        lang: i18n.global.locale.value.toLowerCase(),
        'Accept-Language': i18n.global.locale.value.toLowerCase(),
        ...config.headers,
    };

    // 此处获取token是为H5中webview使用，pc端不再使用
    const token = await getToken(true);
    if (token) {
        config.headers.usertoken = token;
    }

    if (matched) {
        // 处理链接替换，保留一个原始链接别名
        config.urlalias = config.url;
        if (typeof matched.url === 'function') {
            config.url = config.url.replace(matched.key, matched.url(config.url, config));
        } else {
            config.url = config.url.replace(matched.key, matched.url);
        }

        // 处理 headers
        // 1. 如果为空，则忽略
        // 2. 如果为对象，则为默认值，会被请求中的 header 覆盖
        // 3. 如果是方法，则为返回值
        config.headers = mergeObject(matched.headers, config.headers, config);

        // 处理 params
        config.params = mergeObject(matched.params, config.params, config);
    }

    return config;
}, (err) => error(err));

// 响应拦截器
service.interceptors.response.use((res) => {
    if (res && res.data && res.config) report(res);
    const data = transformResponse(res);

    // 登录状态有啥问题时直接跳转到登录页
    if ([10007, 33003, 10009, 30000].includes(data.ret_code)) {
        removeToken();
        reportToken('ajax_error', res);
    }

    // 合规墙 - （用户没有KYC || 本地区不支持此服务）就弹出相关弹窗
    if (data.ret_code === 10024) {
        const { ret_code: retCode, result } = data;
        // 阻止toast弹出信息
        data.message = '';
        showComplianceWall(retCode, result);
    }

    return data;
}, (err) => {
    reportError(err.response ? err.response : err, err.message);
    return error(err);
});

/**
 * POST 请求
 *
 * @param  {...any} args 请求参数
 * @return {Promise}
 */
export const post = (...args) => service.post(...args);

/**
 * GET 请求
 *
 * @param  {...any} args 请求参数
 * @return {Promise}
 */
export const get = (...args) => service.get(...args);
